import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MilkControl } from '@homeleite/domain';
import { EMPTY } from '@homeleite/common-utils';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { firstValueFrom } from 'rxjs';

interface CowProduction {
    id: string;
    name: string;
    lot: string | null;
    liters: number;
}

interface CowComparison extends CowProduction {
    previous: number | null;
}

interface Totals {
    volume: number;
    producing: number;
    average: number;
}

const DROP_ALERT_RATIO = 0.2;
const RANKING_SIZE = 10;
const DRY_OFF_DAYS = 305;
const DAY_MS = 86_400_000;
const NO_LOT = 'Sem lote';
const UP = '#1b5e20';
const DOWN = '#b3261e';
const BAR = '#2e7d32';

const BANDS: { label: string; min: number; max: number }[] = [
    { label: 'Menos de 10 L', min: 0, max: 10 },
    { label: '10 a 20 L', min: 10, max: 20 },
    { label: '20 a 30 L', min: 20, max: 30 },
    { label: 'Mais de 30 L', min: 30, max: Infinity }
];

// Início até 100 dias, pico de 101 a 200 e final acima de 200 dias em lactação.
const PHASES: { label: string; min: number; max: number }[] = [
    { label: 'Início (até 100 dias)', min: 0, max: 100 },
    { label: 'Pico (101 a 200 dias)', min: 101, max: 200 },
    { label: 'Final (mais de 200 dias)', min: 201, max: Infinity }
];

/** Relatório analítico do controle: comparação com o controle anterior, faixas, lactação e lotes. */
@Injectable({
    providedIn: 'root'
})
export class AnalyticReportService {
    private logoBase64: string | null = null;

    constructor(private http: HttpClient) {}

    public async buildAnalyticReport(
        milkControl: MilkControl,
        previous: MilkControl | null
    ): Promise<{ fileName: string; blob: Blob }> {
        await this.loadLogo();

        const current = this.productionByCow(milkControl);
        const before = previous ? this.productionByCow(previous) : null;
        const comparisons: CowComparison[] = [...current.values()].map((cow) => ({
            ...cow,
            previous: before?.get(cow.id)?.liters ?? null
        }));
        const lastBirths: Map<string, Date> | null = null;

        const docDefinition: TDocumentDefinitions = {
            pageSize: 'A4',
            pageMargins: [40, 40, 40, 50],
            footer: (page, pages) => ({
                text: `Relatório Analítico · página ${page} de ${pages}`,
                alignment: 'center',
                fontSize: 8,
                color: '#777777'
            }),
            content: [
                this.buildHeader(milkControl, previous),
                ...this.buildSummary(current, before, previous),
                ...this.buildRanking(comparisons, !!before),
                ...this.buildDropAlert(comparisons, !!before),
                ...this.buildBands(comparisons),
                ...this.buildLactation(comparisons, lastBirths, milkControl),
                ...this.buildLots(current, before)
            ],
            styles: {
                section: { fontSize: 12, bold: true, margin: [0, 16, 0, 6] },
                tableHeader: { bold: true, fontSize: 9, fillColor: '#dedede', alignment: 'center' },
                muted: { fontSize: 9, italics: true, color: '#555555' },
                kpiLabel: { fontSize: 9, color: '#555555', alignment: 'center' },
                kpiValue: { fontSize: 18, bold: true, alignment: 'center' }
            },
            defaultStyle: { fontSize: 9 }
        };

        const pdfMakeModule = await import('pdfmake/build/pdfmake');
        const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
        const pdfMake = (pdfMakeModule.default || pdfMakeModule) as any;
        const pdfFonts = (pdfFontsModule.default || pdfFontsModule) as any;
        pdfMake.addVirtualFileSystem(pdfFonts);

        const date = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
            .format(milkControl.finishedAt ?? milkControl.startedAt)
            .replace(/\//g, '-');
        const blob: Blob = await pdfMake.createPdf(docDefinition).getBlob();
        return { fileName: `RA_${milkControl.producer.code}_${date}.pdf`, blob };
    }

    private async loadLogo(): Promise<void> {
        if (this.logoBase64) return;
        try {
            const blob = await firstValueFrom(this.http.get('assets/images/default-homeleite.png', { responseType: 'blob' }));
            this.logoBase64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = (error) => reject(error);
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error(error);
            this.logoBase64 = null;
        }
    }

    /** Soma das ordenhas por vaca; lote = último informado. */
    private productionByCow(control: MilkControl): Map<string, CowProduction> {
        const cows = new Map<string, CowProduction>();
        (control.milkings ?? []).forEach((milking) =>
            (milking.milkedAnimals ?? []).forEach((milked) => {
                const id = milked.animal?.id;
                if (!id) return;
                const cow = cows.get(id) ?? { id, name: milked.animal.nickname || EMPTY, lot: null, liters: 0 };
                cow.liters += milked.production || 0;
                if (milked.lot && String(milked.lot).trim() !== EMPTY) cow.lot = String(milked.lot).trim();
                cows.set(id, cow);
            })
        );
        return cows;
    }

    private totals(cows: Iterable<CowProduction>): Totals {
        let volume = 0;
        let producing = 0;
        for (const cow of cows) {
            volume += cow.liters;
            if (cow.liters > 0) producing++;
        }
        return { volume, producing, average: producing > 0 ? volume / producing : 0 };
    }

    private buildHeader(control: MilkControl, previous: MilkControl | null): any {
        const date = (value: Date | null | undefined) => (value ? new Date(value).toLocaleDateString('pt-BR') : 'N/A');
        const info = {
            stack: [
                { text: 'RELATÓRIO ANALÍTICO DO CONTROLE LEITEIRO', bold: true, fontSize: 12 },
                { text: `${(control.producer.name ?? EMPTY).toUpperCase()} · Rebanho ${control.producer.code}`, bold: true, margin: [0, 4, 0, 0] },
                { text: `Propriedade: ${control.property?.name || 'Desconhecida'}`, margin: [0, 2, 0, 0] },
                {
                    text: `Controle de ${date(control.startedAt)}${previous ? ` · comparado ao de ${date(previous.startedAt)}` : ' · sem controle anterior para comparar'}`,
                    margin: [0, 2, 0, 0]
                }
            ]
        };
        return {
            columns: this.logoBase64 ? [{ image: this.logoBase64, width: 60 }, { ...info, margin: [12, 0, 0, 0] }] : [info],
            margin: [0, 0, 0, 8]
        };
    }

    private buildSummary(current: Map<string, CowProduction>, before: Map<string, CowProduction> | null, previous: MilkControl | null): any[] {
        const now = this.totals(current.values());
        const old = before ? this.totals(before.values()) : null;
        const kpi = (label: string, value: string, actual: number, prior: number | undefined) => ({
            stack: [
                { text: label, style: 'kpiLabel' },
                { text: value, style: 'kpiValue', margin: [0, 4, 0, 2] },
                old ? this.variation(actual, prior!, { alignment: 'center' }) : { text: '—', style: 'kpiLabel' }
            ]
        });
        return [
            { text: 'Resumo executivo', style: 'section' },
            {
                table: {
                    widths: ['*', '*', '*'],
                    body: [
                        [
                            kpi('Litros totais', `${this.liters(now.volume)} L`, now.volume, old?.volume),
                            kpi('Média por vaca', `${this.liters(now.average)} L`, now.average, old?.average),
                            kpi('Vacas em lactação', String(now.producing), now.producing, old?.producing)
                        ]
                    ]
                },
                layout: this.boxLayout()
            },
            previous ? { text: 'Variação em relação ao controle anterior.', style: 'muted', margin: [0, 4, 0, 0] } : this.noPrevious()
        ];
    }

    private buildRanking(cows: CowComparison[], hasPrevious: boolean): any[] {
        const producing = cows.filter((cow) => cow.liters > 0);
        if (!producing.length) return [{ text: 'Ranking de produção', style: 'section' }, { text: 'Nenhuma vaca com produção neste controle.', style: 'muted' }];

        const sorted = [...producing].sort((a, b) => b.liters - a.liters);
        const top = sorted.slice(0, RANKING_SIZE);
        // Sem sobreposição com o top: com até 10 vacas só há uma lista.
        const bottom = sorted.slice(Math.max(RANKING_SIZE, sorted.length - RANKING_SIZE)).reverse();
        const table = (title: string, rows: CowComparison[]) => ({
            stack: [
                { text: title, bold: true, margin: [0, 0, 0, 4] },
                {
                    table: {
                        headerRows: 1,
                        widths: [16, '*', 40, 50],
                        body: [
                            [
                                { text: '#', style: 'tableHeader' },
                                { text: 'Vaca', style: 'tableHeader' },
                                { text: 'Litros', style: 'tableHeader' },
                                { text: 'Dif.', style: 'tableHeader' }
                            ],
                            ...rows.map((cow) => [
                                { text: String(sorted.indexOf(cow) + 1), alignment: 'center' },
                                cow.name,
                                { text: this.liters(cow.liters), alignment: 'right' },
                                this.difference(cow, hasPrevious)
                            ])
                        ]
                    },
                    layout: 'lightHorizontalLines'
                }
            ]
        });
        return [
            { text: 'Ranking de produção', style: 'section' },
            {
                columns: bottom.length ? [table(`${top.length} maiores`, top), table(`${bottom.length} menores`, bottom)] : [table(`${top.length} maiores`, top), { text: EMPTY }],
                columnGap: 16
            },
            hasPrevious ? { text: 'Dif. = litros em relação ao controle anterior.', style: 'muted', margin: [0, 4, 0, 0] } : this.noPrevious()
        ];
    }

    private buildDropAlert(cows: CowComparison[], hasPrevious: boolean): any[] {
        const title = { text: `Alerta de queda (mais de ${DROP_ALERT_RATIO * 100}% em relação ao controle anterior)`, style: 'section' };
        if (!hasPrevious) return [title, this.noPrevious()];

        const drops = cows
            .filter((cow) => cow.previous != null && cow.previous > 0 && (cow.previous - cow.liters) / cow.previous > DROP_ALERT_RATIO)
            .sort((a, b) => (a.liters - a.previous!) / a.previous! - (b.liters - b.previous!) / b.previous!);
        if (!drops.length) return [title, { text: 'Nenhuma vaca com queda acima do limite.', style: 'muted' }];

        return [
            title,
            {
                table: {
                    headerRows: 1,
                    widths: ['*', 'auto', 60, 60, 60],
                    body: [
                        ['Vaca', 'Lote', 'Anterior', 'Atual', 'Queda'].map((text) => ({ text, style: 'tableHeader' })),
                        ...drops.map((cow) => [
                            cow.name,
                            cow.lot ?? NO_LOT,
                            { text: `${this.liters(cow.previous!)} L`, alignment: 'right' },
                            { text: `${this.liters(cow.liters)} L`, alignment: 'right' },
                            this.variation(cow.liters, cow.previous!, { alignment: 'right' })
                        ])
                    ]
                },
                layout: 'lightHorizontalLines'
            }
        ];
    }

    private buildBands(cows: CowProduction[]): any[] {
        const producing = cows.filter((cow) => cow.liters > 0);
        const title = { text: 'Faixas de produção por vaca', style: 'section' };
        if (!producing.length) return [title, { text: 'Nenhuma vaca com produção neste controle.', style: 'muted' }];

        const counts = BANDS.map((band) => producing.filter((cow) => cow.liters >= band.min && cow.liters < band.max).length);
        const max = Math.max(...counts, 1);
        const barWidth = 300;
        return [
            title,
            {
                table: {
                    widths: [80, barWidth, '*'],
                    body: BANDS.map((band, i) => [
                        { text: band.label, margin: [0, 3, 0, 0] },
                        {
                            canvas: [
                                { type: 'rect', x: 0, y: 0, w: barWidth, h: 14, color: '#eeeeee' },
                                ...(counts[i] ? [{ type: 'rect', x: 0, y: 0, w: (counts[i] / max) * barWidth, h: 14, color: BAR }] : [])
                            ]
                        },
                        { text: `${counts[i]} (${this.percent(counts[i] / producing.length)})`, margin: [0, 3, 0, 0] }
                    ])
                },
                layout: 'noBorders'
            }
        ];
    }

    private buildLactation(cows: CowProduction[], births: Map<string, Date> | null, control: MilkControl): any[] {
        const title = { text: 'Dias em lactação', style: 'section' };
        if (!births) return [title, { text: 'Partos não disponíveis no admin (seção só no aplicativo).', style: 'muted' }];

        const reference = new Date(control.startedAt).getTime();
        const withDays = cows.map((cow) => {
            const birth = births.get(cow.id);
            return { cow, days: birth ? Math.floor((reference - birth.getTime()) / DAY_MS) : null };
        });
        const phaseRows = PHASES.map((phase) => {
            const inPhase = withDays.filter((it) => it.days != null && it.days >= phase.min && it.days <= phase.max);
            const total = this.totals(inPhase.map((it) => it.cow));
            return [phase.label, { text: String(inPhase.length), alignment: 'center' }, { text: `${this.liters(total.volume)} L`, alignment: 'right' }, { text: `${this.liters(total.average)} L`, alignment: 'right' }];
        });
        const unknown = withDays.filter((it) => it.days == null).length;
        const candidates = withDays.filter((it) => it.days != null && it.days > DRY_OFF_DAYS).sort((a, b) => b.days! - a.days!);

        return [
            title,
            {
                table: {
                    headerRows: 1,
                    widths: ['*', 50, 70, 70],
                    body: [['Fase', 'Vacas', 'Litros', 'Média'].map((text) => ({ text, style: 'tableHeader' })), ...phaseRows]
                },
                layout: 'lightHorizontalLines'
            },
            unknown ? { text: `${unknown} vaca(s) sem parto registrado neste aparelho (fora das fases).`, style: 'muted', margin: [0, 4, 0, 0] } : null,
            { text: `Candidatas à secagem (mais de ${DRY_OFF_DAYS} dias em lactação)`, bold: true, margin: [0, 10, 0, 4] },
            candidates.length
                ? {
                      table: {
                          headerRows: 1,
                          widths: ['*', 'auto', 60, 60],
                          body: [
                              ['Vaca', 'Lote', 'Dias', 'Litros'].map((text) => ({ text, style: 'tableHeader' })),
                              ...candidates.map(({ cow, days }) => [
                                  cow.name,
                                  cow.lot ?? NO_LOT,
                                  { text: String(days), alignment: 'center' },
                                  { text: `${this.liters(cow.liters)} L`, alignment: 'right' }
                              ])
                          ]
                      },
                      layout: 'lightHorizontalLines'
                  }
                : { text: 'Nenhuma vaca acima do limite.', style: 'muted' }
        ].filter(Boolean);
    }

    private buildLots(current: Map<string, CowProduction>, before: Map<string, CowProduction> | null): any[] {
        const group = (cows: Map<string, CowProduction>) => {
            const lots = new Map<string, CowProduction[]>();
            cows.forEach((cow) => lots.set(cow.lot ?? NO_LOT, [...(lots.get(cow.lot ?? NO_LOT) ?? []), cow]));
            return lots;
        };
        const now = group(current);
        const old = before ? group(before) : null;
        const names = [...now.keys()].sort((a, b) => (a === NO_LOT ? 1 : b === NO_LOT ? -1 : a.localeCompare(b, undefined, { numeric: true })));
        const title = { text: 'Comparativo entre lotes', style: 'section' };
        if (!names.length) return [title, { text: 'Nenhuma pesagem neste controle.', style: 'muted' }];

        const rows = names.map((name) => {
            const actual = this.totals(now.get(name)!);
            const prior = old?.get(name) ? this.totals(old.get(name)!) : null;
            return [
                name,
                { text: String(actual.producing), alignment: 'center' },
                { text: `${this.liters(actual.volume)} L`, alignment: 'right' },
                prior ? this.variation(actual.volume, prior.volume, { alignment: 'right' }) : { text: '—', alignment: 'center' },
                { text: `${this.liters(actual.average)} L`, alignment: 'right' },
                prior ? this.variation(actual.average, prior.average, { alignment: 'right' }) : { text: '—', alignment: 'center' }
            ];
        });
        return [
            title,
            {
                table: {
                    headerRows: 1,
                    widths: ['*', 40, 60, 60, 60, 60],
                    body: [['Lote', 'Vacas', 'Volume', 'Var. vol.', 'Média', 'Var. média'].map((text) => ({ text, style: 'tableHeader' })), ...rows]
                },
                layout: 'lightHorizontalLines'
            },
            old
                ? { text: 'Lote do controle anterior com o mesmo nome; "—" quando o lote não existia.', style: 'muted', margin: [0, 4, 0, 0] }
                : this.noPrevious()
        ];
    }

    /** Triângulo + sinal + cor: cor nunca é o único indicador. */
    private variation(actual: number, prior: number, extra: { alignment?: 'center' | 'right' } = {}): any {
        if (!prior) return { text: '—', ...extra };
        const ratio = (actual - prior) / prior;
        if (Math.abs(ratio) < 0.0005) return { text: '0,0%', ...extra };
        return this.trend(ratio > 0, `${ratio > 0 ? '+' : ''}${this.percent(ratio)}`, extra.alignment ?? 'right');
    }

    private difference(cow: CowComparison, hasPrevious: boolean): any {
        if (!hasPrevious) return { text: '—', alignment: 'center' };
        if (cow.previous == null) return { text: 'nova', alignment: 'center', italics: true };
        const diff = cow.liters - cow.previous;
        if (Math.abs(diff) < 0.05) return { text: '0,0', alignment: 'right' };
        return this.trend(diff > 0, `${diff > 0 ? '+' : ''}${this.liters(diff)}`, 'right');
    }

    /** A Roboto do pdfmake não tem ▲/▼: o triângulo é desenhado em vetor. */
    private trend(up: boolean, text: string, alignment: 'center' | 'right'): any {
        const color = up ? UP : DOWN;
        const points = up
            ? [{ x: 0, y: 7 }, { x: 3.5, y: 1 }, { x: 7, y: 7 }]
            : [{ x: 0, y: 1 }, { x: 3.5, y: 7 }, { x: 7, y: 1 }];
        const spacer = { width: '*', text: EMPTY };
        return {
            columns: [
                spacer,
                { width: 7, canvas: [{ type: 'polyline', closePath: true, points, color, lineColor: color }], margin: [0, 1, 0, 0] },
                { width: 'auto', text, color, bold: true },
                ...(alignment === 'center' ? [spacer] : [])
            ],
            columnGap: 3
        };
    }

    private noPrevious(): any {
        return { text: 'Sem controle anterior para comparar.', style: 'muted', margin: [0, 4, 0, 0] };
    }

    private boxLayout(): any {
        return {
            hLineColor: () => '#aaaaaa',
            vLineColor: () => '#aaaaaa',
            paddingTop: () => 8,
            paddingBottom: () => 8
        };
    }

    private liters(value: number): string {
        return value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    }

    private percent(ratio: number): string {
        return `${(ratio * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
    }
}
